/**
 * Country / operating-currency helpers (frontend mirror of backend presets).
 * Prefers ConfigContext countryPresets when available.
 * Full ISO country list — keep in sync with backend/country_presets.py.
 */

/** ISO 3166-1 alpha-2 → regional-indicator flag emoji (🇨🇭, 🇬🇧, …). */
export function countryFlagEmoji(countryCode) {
  const cc = String(countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const A = 0x1f1e6;
  return String.fromCodePoint(A + cc.charCodeAt(0) - 65, A + cc.charCodeAt(1) - 65);
}

export function countryDisplayLabel(countryCode, presets) {
  const code = String(countryCode || 'CH').toUpperCase();
  const table = presets || FALLBACK_COUNTRY_PRESETS;
  const label = table[code]?.label || code;
  const flag = countryFlagEmoji(code);
  return flag ? `${flag} ${label}` : label;
}

const _g = { taxLabel: 'Tax', taxRate: 0.25, notaryFeesPercent: 0.02, marketReferencePricePerSqm: 5000 };
const _p = (label, operatingCurrency, extra = {}) => ({
  label,
  operatingCurrency,
  ..._g,
  ...extra,
});

/** Offline fallback — keep in sync with backend/country_presets.py */
export const FALLBACK_COUNTRY_PRESETS = {
  AD: _p('Andorra', 'EUR'),
  AE: _p('United Arab Emirates', 'AED', { taxLabel: 'CIT', taxRate: 0.09, notaryFeesPercent: 0.02, marketReferencePricePerSqm: 12000 }),
  AF: _p('Afghanistan', 'AFN'),
  AG: _p('Antigua and Barbuda', 'XCD'),
  AI: _p('Anguilla', 'XCD'),
  AL: _p('Albania', 'ALL'),
  AM: _p('Armenia', 'AMD'),
  AO: _p('Angola', 'AOA'),
  AQ: _p('Antarctica', 'USD'),
  AR: _p('Argentina', 'ARS'),
  AS: _p('American Samoa', 'USD'),
  AT: _p('Austria', 'EUR'),
  AU: _p('Australia', 'AUD'),
  AW: _p('Aruba', 'AWG'),
  AX: _p('Aland Islands', 'EUR'),
  AZ: _p('Azerbaijan', 'AZN'),
  BA: _p('Bosnia and Herzegovina', 'BAM'),
  BB: _p('Barbados', 'BBD'),
  BD: _p('Bangladesh', 'BDT'),
  BE: _p('Belgium', 'EUR'),
  BF: _p('Burkina Faso', 'XOF'),
  BG: _p('Bulgaria', 'BGN'),
  BH: _p('Bahrain', 'BHD'),
  BI: _p('Burundi', 'BIF'),
  BJ: _p('Benin', 'XOF'),
  BL: _p('Saint Barthelemy', 'EUR'),
  BM: _p('Bermuda', 'BMD'),
  BN: _p('Brunei', 'BND'),
  BO: _p('Bolivia', 'BOB'),
  BQ: _p('Bonaire, Saint Eustatius and Saba ', 'USD'),
  BR: _p('Brazil', 'BRL'),
  BS: _p('Bahamas', 'BSD'),
  BT: _p('Bhutan', 'BTN'),
  BV: _p('Bouvet Island', 'NOK'),
  BW: _p('Botswana', 'BWP'),
  BY: _p('Belarus', 'BYR'),
  BZ: _p('Belize', 'BZD'),
  CA: _p('Canada', 'CAD'),
  CC: _p('Cocos Islands', 'AUD'),
  CD: _p('Democratic Republic of the Congo', 'CDF'),
  CF: _p('Central African Republic', 'XAF'),
  CG: _p('Republic of the Congo', 'XAF'),
  CH: _p('Switzerland', 'CHF', { taxLabel: 'IBGI', taxRate: 0.147, notaryFeesPercent: 0.05, marketReferencePricePerSqm: 15000 }),
  CI: _p('Ivory Coast', 'XOF'),
  CK: _p('Cook Islands', 'NZD'),
  CL: _p('Chile', 'CLP'),
  CM: _p('Cameroon', 'XAF'),
  CN: _p('China', 'CNY'),
  CO: _p('Colombia', 'COP'),
  CR: _p('Costa Rica', 'CRC'),
  CU: _p('Cuba', 'CUP'),
  CV: _p('Cape Verde', 'CVE'),
  CW: _p('Curacao', 'ANG'),
  CX: _p('Christmas Island', 'AUD'),
  CY: _p('Cyprus', 'EUR'),
  CZ: _p('Czech Republic', 'CZK'),
  DE: _p('Germany', 'EUR', { taxLabel: 'KSt', taxRate: 0.3, notaryFeesPercent: 0.015, marketReferencePricePerSqm: 7000 }),
  DJ: _p('Djibouti', 'DJF'),
  DK: _p('Denmark', 'DKK'),
  DM: _p('Dominica', 'XCD'),
  DO: _p('Dominican Republic', 'DOP'),
  DZ: _p('Algeria', 'DZD'),
  EC: _p('Ecuador', 'USD'),
  EE: _p('Estonia', 'EUR'),
  EG: _p('Egypt', 'EGP'),
  EH: _p('Western Sahara', 'MAD'),
  ER: _p('Eritrea', 'ERN'),
  ES: _p('Spain', 'EUR', { taxLabel: 'IS', taxRate: 0.25, notaryFeesPercent: 0.015, marketReferencePricePerSqm: 4500 }),
  ET: _p('Ethiopia', 'ETB'),
  FI: _p('Finland', 'EUR'),
  FJ: _p('Fiji', 'FJD'),
  FK: _p('Falkland Islands', 'FKP'),
  FM: _p('Micronesia', 'USD'),
  FO: _p('Faroe Islands', 'DKK'),
  FR: _p('France', 'EUR', { taxLabel: 'IS', taxRate: 0.25, notaryFeesPercent: 0.08, marketReferencePricePerSqm: 8000 }),
  GA: _p('Gabon', 'XAF'),
  GB: _p('United Kingdom', 'GBP', { taxLabel: 'Corp. tax', taxRate: 0.25, notaryFeesPercent: 0.01, marketReferencePricePerSqm: 9000 }),
  GD: _p('Grenada', 'XCD'),
  GE: _p('Georgia', 'GEL'),
  GF: _p('French Guiana', 'EUR'),
  GG: _p('Guernsey', 'GBP'),
  GH: _p('Ghana', 'GHS'),
  GI: _p('Gibraltar', 'GIP'),
  GL: _p('Greenland', 'DKK'),
  GM: _p('Gambia', 'GMD'),
  GN: _p('Guinea', 'GNF'),
  GP: _p('Guadeloupe', 'EUR'),
  GQ: _p('Equatorial Guinea', 'XAF'),
  GR: _p('Greece', 'EUR'),
  GS: _p('South Georgia and the South Sandwich Islands', 'GBP'),
  GT: _p('Guatemala', 'GTQ'),
  GU: _p('Guam', 'USD'),
  GW: _p('Guinea-Bissau', 'XOF'),
  GY: _p('Guyana', 'GYD'),
  HK: _p('Hong Kong', 'HKD'),
  HM: _p('Heard Island and McDonald Islands', 'AUD'),
  HN: _p('Honduras', 'HNL'),
  HR: _p('Croatia', 'HRK'),
  HT: _p('Haiti', 'HTG'),
  HU: _p('Hungary', 'HUF'),
  ID: _p('Indonesia', 'IDR'),
  IE: _p('Ireland', 'EUR'),
  IL: _p('Israel', 'ILS'),
  IM: _p('Isle of Man', 'GBP'),
  IN: _p('India', 'INR'),
  IO: _p('British Indian Ocean Territory', 'USD'),
  IQ: _p('Iraq', 'IQD'),
  IR: _p('Iran', 'IRR'),
  IS: _p('Iceland', 'ISK'),
  IT: _p('Italy', 'EUR', { taxLabel: 'IRES', taxRate: 0.24, notaryFeesPercent: 0.03, marketReferencePricePerSqm: 5500 }),
  JE: _p('Jersey', 'GBP'),
  JM: _p('Jamaica', 'JMD', { marketReferencePricePerSqm: 350000 }),
  JO: _p('Jordan', 'JOD'),
  JP: _p('Japan', 'JPY'),
  KE: _p('Kenya', 'KES'),
  KG: _p('Kyrgyzstan', 'KGS'),
  KH: _p('Cambodia', 'KHR'),
  KI: _p('Kiribati', 'AUD'),
  KM: _p('Comoros', 'KMF'),
  KN: _p('Saint Kitts and Nevis', 'XCD'),
  KP: _p('North Korea', 'KPW'),
  KR: _p('South Korea', 'KRW'),
  KW: _p('Kuwait', 'KWD'),
  KY: _p('Cayman Islands', 'KYD'),
  KZ: _p('Kazakhstan', 'KZT'),
  LA: _p('Laos', 'LAK'),
  LB: _p('Lebanon', 'LBP'),
  LC: _p('Saint Lucia', 'XCD'),
  LI: _p('Liechtenstein', 'CHF'),
  LK: _p('Sri Lanka', 'LKR'),
  LR: _p('Liberia', 'LRD'),
  LS: _p('Lesotho', 'LSL'),
  LT: _p('Lithuania', 'LTL'),
  LU: _p('Luxembourg', 'EUR'),
  LV: _p('Latvia', 'EUR'),
  LY: _p('Libya', 'LYD'),
  MA: _p('Morocco', 'MAD'),
  MC: _p('Monaco', 'EUR'),
  MD: _p('Moldova', 'MDL'),
  ME: _p('Montenegro', 'EUR'),
  MF: _p('Saint Martin', 'EUR'),
  MG: _p('Madagascar', 'MGA'),
  MH: _p('Marshall Islands', 'USD'),
  MK: _p('Macedonia', 'MKD'),
  ML: _p('Mali', 'XOF'),
  MM: _p('Myanmar', 'MMK'),
  MN: _p('Mongolia', 'MNT'),
  MO: _p('Macao', 'MOP'),
  MP: _p('Northern Mariana Islands', 'USD'),
  MQ: _p('Martinique', 'EUR'),
  MR: _p('Mauritania', 'MRO'),
  MS: _p('Montserrat', 'XCD'),
  MT: _p('Malta', 'EUR'),
  MU: _p('Mauritius', 'MUR'),
  MV: _p('Maldives', 'MVR'),
  MW: _p('Malawi', 'MWK'),
  MX: _p('Mexico', 'MXN'),
  MY: _p('Malaysia', 'MYR'),
  MZ: _p('Mozambique', 'MZN'),
  NA: _p('Namibia', 'NAD'),
  NC: _p('New Caledonia', 'XPF'),
  NE: _p('Niger', 'XOF'),
  NF: _p('Norfolk Island', 'AUD'),
  NG: _p('Nigeria', 'NGN'),
  NI: _p('Nicaragua', 'NIO'),
  NL: _p('Netherlands', 'EUR'),
  NO: _p('Norway', 'NOK'),
  NP: _p('Nepal', 'NPR'),
  NR: _p('Nauru', 'AUD'),
  NU: _p('Niue', 'NZD'),
  NZ: _p('New Zealand', 'NZD'),
  OM: _p('Oman', 'OMR'),
  PA: _p('Panama', 'PAB'),
  PE: _p('Peru', 'PEN'),
  PF: _p('French Polynesia', 'XPF'),
  PG: _p('Papua New Guinea', 'PGK'),
  PH: _p('Philippines', 'PHP'),
  PK: _p('Pakistan', 'PKR'),
  PL: _p('Poland', 'PLN'),
  PM: _p('Saint Pierre and Miquelon', 'EUR'),
  PN: _p('Pitcairn', 'NZD'),
  PR: _p('Puerto Rico', 'USD'),
  PS: _p('Palestinian Territory', 'ILS'),
  PT: _p('Portugal', 'EUR'),
  PW: _p('Palau', 'USD'),
  PY: _p('Paraguay', 'PYG'),
  QA: _p('Qatar', 'QAR'),
  RE: _p('Reunion', 'EUR'),
  RO: _p('Romania', 'RON'),
  RS: _p('Serbia', 'RSD'),
  RU: _p('Russia', 'RUB'),
  RW: _p('Rwanda', 'RWF'),
  SA: _p('Saudi Arabia', 'SAR'),
  SB: _p('Solomon Islands', 'SBD'),
  SC: _p('Seychelles', 'SCR'),
  SD: _p('Sudan', 'SDG'),
  SE: _p('Sweden', 'SEK'),
  SG: _p('Singapore', 'SGD'),
  SH: _p('Saint Helena', 'SHP'),
  SI: _p('Slovenia', 'EUR'),
  SJ: _p('Svalbard and Jan Mayen', 'NOK'),
  SK: _p('Slovakia', 'EUR'),
  SL: _p('Sierra Leone', 'SLL'),
  SM: _p('San Marino', 'EUR'),
  SN: _p('Senegal', 'XOF'),
  SO: _p('Somalia', 'SOS'),
  SR: _p('Suriname', 'SRD'),
  SS: _p('South Sudan', 'SSP'),
  ST: _p('Sao Tome and Principe', 'STD'),
  SV: _p('El Salvador', 'USD'),
  SX: _p('Sint Maarten', 'ANG'),
  SY: _p('Syria', 'SYP'),
  SZ: _p('Swaziland', 'SZL'),
  TC: _p('Turks and Caicos Islands', 'USD'),
  TD: _p('Chad', 'XAF'),
  TF: _p('French Southern Territories', 'EUR'),
  TG: _p('Togo', 'XOF'),
  TH: _p('Thailand', 'THB'),
  TJ: _p('Tajikistan', 'TJS'),
  TK: _p('Tokelau', 'NZD'),
  TL: _p('East Timor', 'USD'),
  TM: _p('Turkmenistan', 'TMT'),
  TN: _p('Tunisia', 'TND'),
  TO: _p('Tonga', 'TOP'),
  TR: _p('Turkey', 'TRY'),
  TT: _p('Trinidad and Tobago', 'TTD'),
  TV: _p('Tuvalu', 'AUD'),
  TW: _p('Taiwan', 'TWD'),
  TZ: _p('Tanzania', 'TZS'),
  UA: _p('Ukraine', 'UAH'),
  UG: _p('Uganda', 'UGX'),
  UM: _p('United States Minor Outlying Islands', 'USD'),
  US: _p('United States', 'USD', { taxLabel: 'Fed+state', taxRate: 0.21, notaryFeesPercent: 0.01, marketReferencePricePerSqm: 6000 }),
  UY: _p('Uruguay', 'UYU'),
  UZ: _p('Uzbekistan', 'UZS'),
  VA: _p('Vatican', 'EUR'),
  VC: _p('Saint Vincent and the Grenadines', 'XCD'),
  VE: _p('Venezuela', 'VEF'),
  VG: _p('British Virgin Islands', 'USD'),
  VI: _p('U.S. Virgin Islands', 'USD'),
  VN: _p('Vietnam', 'VND'),
  VU: _p('Vanuatu', 'VUV'),
  WF: _p('Wallis and Futuna', 'XPF'),
  WS: _p('Samoa', 'WST'),
  XK: _p('Kosovo', 'EUR'),
  YE: _p('Yemen', 'YER'),
  YT: _p('Mayotte', 'EUR'),
  ZA: _p('South Africa', 'ZAR'),
  ZM: _p('Zambia', 'ZMK'),
  ZW: _p('Zimbabwe', 'ZWL'),
};

export const FALLBACK_CURRENCIES = [...new Set(
  Object.values(FALLBACK_COUNTRY_PRESETS).map((p) => p.operatingCurrency).concat(['CHF', 'EUR', 'GBP', 'USD', 'AED']),
)].sort();

export function applyCountryLocals(assumptions, countryCode, presets) {
  const code = (countryCode || 'CH').toUpperCase();
  const table = presets || FALLBACK_COUNTRY_PRESETS;
  const preset = table[code];
  if (!preset) {
    return { ...assumptions, countryCode: code };
  }
  return {
    ...assumptions,
    countryCode: code,
    operatingCurrency: preset.operatingCurrency,
    taxLabel: preset.taxLabel,
    taxRate: preset.taxRate,
    notaryFeesPercent: preset.notaryFeesPercent,
    marketReferencePricePerSqm: preset.marketReferencePricePerSqm,
    reportingCurrency: 'CHF',
  };
}

export function setCountryCurrencyOnly(assumptions, countryCode, presets) {
  const code = (countryCode || 'CH').toUpperCase();
  const table = presets || FALLBACK_COUNTRY_PRESETS;
  const preset = table[code];
  return {
    ...assumptions,
    countryCode: code,
    operatingCurrency: preset?.operatingCurrency || assumptions.operatingCurrency || 'CHF',
    reportingCurrency: 'CHF',
  };
}

